import { Row, Col, Card, Table, Button, Input, Tag } from "antd";
import React, { useState, useEffect, useRef } from "react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import useDebounce from "../../hooks/useDebounce";
import ShowTotal from "../../components/ShowTotal";
import apiPath from "../../constants/apiPath";
import moment from "moment";
import { getSortOrder } from "../../constants/conts";
import AdjustModal from "./AdjustModal";
const Search = Input.Search;

const SOURCE_COLORS = {
  trial: "blue",
  subscription: "green",
  order: "volcano",
  purchase: "gold",
  manual: "purple",
  expired: "red",
};

function Index() {

  const sectionName = "Credit Transaction";

  const api = { list: apiPath.listCreditTransaction };

  const [searchText, setSearchText] = useState("");
  const { request } = useRequest();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [sortBy, setSortBy] = useState("");
  const lastFiltersRef = useRef({});
  const debouncedSearchText = useDebounce(searchText, 300);
  const [adjustOpen, setAdjustOpen] = useState(false);

  const columns = [
    {
      title: "User",
      key: "user_name",
      render: (_, { user_name, user_phone }) => (
        <div>
          <span className="cap">{user_name || "-"}</span>
          {user_phone ? <div style={{ fontSize: 12, color: "#888" }}>{user_phone}</div> : null}
        </div>
      ),
    },
    {
      title: "Credits",
      dataIndex: "amount",
      key: "amount",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "amount"),
      render: (_, { amount }) => (
        <span style={{ fontWeight: 700, color: amount >= 0 ? "#2e7d32" : "#c62828" }}>
          {amount >= 0 ? `+${amount}` : amount}
        </span>
      ),
    },
    {
      title: "Source",
      key: "source",
      filters: [
        { text: "Trial", value: "trial" },
        { text: "Subscription", value: "subscription" },
        { text: "Order", value: "order" },
        { text: "Purchase", value: "purchase" },
        { text: "Manual", value: "manual" },
        { text: "Expired", value: "expired" },
      ],
      render: (_, { source }) => <Tag color={SOURCE_COLORS[source] || "default"} className="cap">{source || "-"}</Tag>,
    },
    {
      title: "Reference",
      key: "reference_id",
      render: (_, { reference_type, reference_id }) =>
        reference_id ? (
          <span style={{ fontSize: 12 }}>
            {reference_type ? `${reference_type}: ` : ""}{String(reference_id).slice(-8).toUpperCase()}
          </span>
        ) : "-",
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      render: (_, { reason }) => reason || "-",
    },
    {
      title: "Balance After",
      dataIndex: "balance_after",
      key: "balance_after",
      render: (_, { balance_after }) => <span style={{ fontWeight: 600 }}>{balance_after ?? "-"}</span>,
    },
    {
      title: "Date",
      key: "created_at",
      dataIndex: "created_at",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "created_at"),
      render: (_, { created_at }) => moment(created_at).format("DD-MMM-YYYY HH:mm"),
    },
  ];

  useEffect(() => {
    setLoading(true);
    fetchData(pagination, lastFiltersRef.current, sortBy);
  }, [refresh, debouncedSearchText, sortBy, pagination.current, pagination.pageSize]);

  const fetchData = (pagination, filters, sortValue = "") => {
    const filterSource = filters ? filters.source : null;
    request({
      url:
        api.list +
        `?source=${filterSource ? filterSource.join(",") : ""}` +
        `&page=${pagination.current}` +
        `&limit=${pagination.pageSize}` +
        `&search=${debouncedSearchText}` +
        `&sort=${sortValue || ""}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        setList(data.data.list.docs);
        setPagination((prev) => ({
          ...prev,
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: data.data.list.totalDocs,
        }));
      },
      onError: (error) => {
        console.log(error);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const handleChange = (pag, filters, sorter) => {
    const { order, field } = sorter || {};
    let nextSort = "";
    if (order) nextSort = `${field}:${order === "ascend" ? "asc" : "desc"}`;
    lastFiltersRef.current = filters || {};
    setSortBy(nextSort);
    setPagination((prev) => ({
      ...prev,
      current: pag?.current || 1,
      pageSize: pag?.pageSize || prev.pageSize,
    }));
  };

  const onSearch = (e) => {
    setSearchText(e.target.value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  return (
    <>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <Card
              variant={false}
              className="criclebox tablespace mb-24"
              title={sectionName + " Management"}
              extra={
                <>
                  <Search size="large" onChange={onSearch} value={searchText} onPressEnter={onSearch} placeholder="Search by user or reason" />
                  <Button type="primary" onClick={() => setAdjustOpen(true)}>Adjust Credits</Button>
                </>
              }
            >
              <h4 className="text-right mb-1">{pagination.total ? ShowTotal(pagination.total) : ShowTotal(0)}</h4>
              <div className="table-responsive customPagination">
                <Table
                  loading={loading}
                  columns={columns}
                  dataSource={list}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    responsive: true,
                    defaultPageSize: 20,
                    pageSizeOptions: ["10", "20", "30", "50", "100"],
                  }}
                  onChange={handleChange}
                  className="ant-border-space"
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {adjustOpen && (
        <AdjustModal
          show={adjustOpen}
          hide={() => setAdjustOpen(false)}
          refresh={() => setRefresh((prev) => !prev)}
        />
      )}
    </>
  );
}

export default Index;
